import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { Edit } from "lucide-react";
import UnauthorizedPage from "../../UnauthorizedPage";
import ErrorNotification from "../../ErrorNotification";
import AddResourceSchedulerAccount from "./AddResourceSchedulerAccount";
import { CircularProgress } from "@mui/material"; // Import CircularProgress from MUI

const apiUrl = process.env.REACT_APP_API_URL;

export default function ResourceSchedulerConfig() {
  let accessToken = useSelector((state) => state.accessToken.value);
  const [addAccountOpen, setAddAccountOpen] = useState(false);
  const [data, setData] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [tooltip, setTooltip] = useState({ visible: false, x: 0, y: 0 });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true); // Add loading state

  const getData = async () => {
    setLoading(true); // Start loading
    const query = `
      query MyQuery {
        getAccounts {
          AccountName
          AccountNo
          id
          region
          lastUpdated
          portalUrl
          scheduledescription
          startecscronexpression
          startrdscronexpression
          status
          stopcronexpression
          timezone
          username
          apiUrl
          startTime
          stopTime
          days
          stateStop
          stateStart
        }
      }
    `;

    try {
      const response = await axios.post(
        apiUrl,
        { query },
        {
          headers: {
            Authorization: accessToken,
          },
        }
      );
      if (response.data.data && response.data.data.getAccounts)
        setData(response.data.data.getAccounts);
      if (
        response.data.errors &&
        response.data.errors[0].errorType == "Unauthorized"
      )
        setError("Unauthorized");
    } catch (error) {
      console.log(error);
    }
    setLoading(false); // End loading
  };

  useEffect(() => {
    getData();
  }, [refresh]);

  const handleEdit = (row) => {
    setEditingId(row.id);
    setEditData({
      username: row.username,
      password: row.password,
      apiUrl: row.apiUrl,
      portalUrl: row.portalUrl,
    });
  };

  const handleSubmit = async (id) => {
    const query = `
  mutation MyMutation($apiUrl:String, $id:String, $password:String, $portalUrl:String, $username:String) {
  updateAwsAccountRS(
    apiUrl: $apiUrl
    id: $id
    password: $password
    portalUrl: $portalUrl
    username: $username
  )
}
  `;

    const variables = {
      apiUrl: editData.apiUrl,
      id: id,
      password: editData.password,
      portalUrl: editData.portalUrl,
      username: editData.username,
    };
    try {
      await axios
        .post(
          apiUrl,
          { query, variables },
          {
            headers: {
              Authorization: accessToken,
            },
          }
        )
        .then((response) => {
          if (
            response.data.errors &&
            response.data.errors[0].errorType == "Unauthorized"
          )
            setError(
              "Edit access is not granted to your account for this resource."
            );
        })
        .catch(() => console.log("error"));
      setRefresh(!refresh);
    } catch (error) {
      console.log(error);
    }
    setEditingId(null);
    setEditData({});
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditData({});
  };

  const handleChange = (e, field) => {
    setEditData({
      ...editData,
      [field]: e.target.value,
    });
  };

  if (error == "Unauthorized") {
    return <UnauthorizedPage />;
  }

  return (
    <div className="p-4 bg-gradient-to-b from-[#003366]/5 to-white">
      {error && (
        <ErrorNotification message={error} onClose={() => setError(null)} />
      )}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-[#003366]">AWS Accounts</h1>
          <button
            onClick={() => setAddAccountOpen(true)}
            className="px-4 py-2 bg-[#003366] text-white rounded-md hover:bg-[#003366]/90 transition-colors flex items-center gap-2"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                clipRule="evenodd"
              />
            </svg>
            Add Account
          </button>
        </div>

        <div className="overflow-x-auto">
          {loading ? ( // Show loading spinner if loading
            <div className="flex justify-center items-center py-16">
              <CircularProgress size={40} style={{ color: "#003366" }} />
            </div>
          ) : (
            <>
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-[#003366]/5">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                      Account Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                      Account Number
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                      Region
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                      Portal
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                      Api
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                      Username
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                      Password
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-[#003366] uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data?.map((row) => (
                    <tr
                      key={row.id}
                      className="hover:bg-[#003366]/5 transition-colors"
                    >
                      <td className="px-6 py-4 whitespace-nowrap text-[#003366] ">
                        {row.AccountName}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                        {row.AccountNo}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                        {row.region}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                        {editingId === row.id ? (
                          <input
                            type="text"
                            value={editData.portalUrl || ""}
                            onChange={(e) => handleChange(e, "portalUrl")}
                            className="border rounded px-2 py-1 w-full"
                          />
                        ) : (
                          row.portalUrl
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                        {editingId === row.id ? (
                          <input
                            type="text"
                            value={editData.apiUrl || ""}
                            onChange={(e) => handleChange(e, "apiUrl")}
                            className="border rounded px-2 py-1 w-full"
                          />
                        ) : (
                          row.apiUrl
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                        {editingId === row.id ? (
                          <input
                            type="text"
                            value={editData.username || ""}
                            onChange={(e) => handleChange(e, "username")}
                            className="border rounded px-2 py-1 w-full"
                          />
                        ) : (
                          row.username
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                        {editingId === row.id ? (
                          <input
                            type="text"
                            value={editData.password || ""}
                            onChange={(e) => handleChange(e, "password")}
                            className="border rounded px-2 py-1 w-full"
                          />
                        ) : (
                          "[protected]"
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-gray-700">
                        {editingId === row.id ? (
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleSubmit(row.id)}
                              className={
                                !editData.password || editData.password == ""
                                  ? "text-gray-600"
                                  : `text-green-600 hover:text-green-800 font-medium`
                              }
                              disabled={
                                !editData.password || editData.password == ""
                              }
                            >
                              Save
                            </button>
                            <button
                              onClick={handleCancel}
                              className="text-red-600 hover:text-red-800 font-medium"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => handleEdit(row)}
                            className="flex items-center gap-2 px-4 py-2 bg-[#003366] text-white rounded hover:bg-[#004488]"
                            onMouseEnter={(e) => {
                              const rect =
                                e.currentTarget.getBoundingClientRect();
                              setTooltip({
                                visible: true,
                                x: rect.left + rect.width / 2,
                                y: rect.bottom + 5,
                              });
                            }}
                            onMouseLeave={() =>
                              setTooltip({ visible: false, x: 0, y: 0 })
                            }
                          >
                            <Edit className="w-4 h-4" />
                            Edit
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {data?.length === 0 && (
                <div className="text-center py-12">
                  <div className="text-[#003366]">
                    <svg
                      className="w-12 h-12 mx-auto mb-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M20 12H4"
                      />
                    </svg>
                    <p className="text-lg font-semibold">No Data Available</p>
                    <p className="mt-2">
                      Click the Add New button to add your first item
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {tooltip.visible && (
          <div
            style={{
              position: "fixed",
              left: `${tooltip.x}px`,
              top: `${tooltip.y}px`,
              transform: "translateX(-50%)",
              zIndex: 1000,
            }}
            className="bg-black text-white text-sm rounded px-2 py-1"
          >
            A password must be provided when making edits
          </div>
        )}

        <AddResourceSchedulerAccount
          open={addAccountOpen}
          onClose={() => setAddAccountOpen(false)}
          refresh={refresh}
          setRefresh={setRefresh}
        />
      </div>
    </div>
  );
}
